import { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Config from "@hero/shared/models/config";
import { ISprint } from "@hero/shared/models/sprint";
import { ITask } from "@hero/shared/models/task";

// Từ Review Code trở đi = "hoàn thành phần việc chính" — khớp với server COMPLETED_STATUSES
const DONE_STATUSES = [
    Config.COLUMN_STATUS.REVIEW_CODE.mongoId,
    Config.COLUMN_STATUS.QA_TESTING.mongoId,
    Config.COLUMN_STATUS.TO_DEPLOY.mongoId,
    Config.COLUMN_STATUS.PROD_VALIDATION.mongoId,
    Config.COLUMN_STATUS.DONE.mongoId,
];
const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

interface CategoryData {
    key: string;
    label: string;
    color: string;
    tasks: any[];
}

const TaskOverviewWidget: FC<{ userId: string; sprint: ISprint }> = ({ userId, sprint }) => {
    const tasks: ITask[] = useSelector((state: any) => state.taskState.tasks ?? []);
    const [expanded, setExpanded] = useState<string | null>(null);

    const categories = useMemo<CategoryData[]>(() => {
        const now = Date.now();
        const userTasks = tasks.filter((t: any) => {
            const assigneeId = t.assignee?._id?.toString() || t.assignee?.toString() || t.assigneeId;
            const sprintId = t.sprint?._id?.toString() || t.sprint?.toString() || t.sprintId;
            return assigneeId === userId && sprintId === sprint._id;
        });

        const nearDeadline: any[] = [];
        const overdue: any[] = [];
        const inProgress: any[] = [];
        const todoIdle: any[] = [];

        for (const t of userTasks) {
            const statusId = (t.status as any)?._id?.toString() || t.status?.toString() || "";
            if (DONE_STATUSES.includes(statusId)) continue;

            if (statusId === Config.COLUMN_STATUS.IN_PROGRESS.mongoId) inProgress.push(t);
            if (statusId === Config.COLUMN_STATUS.TO_DO.mongoId) {
                const created = (t as any).createdTime ? new Date((t as any).createdTime).getTime() : 0;
                if (created && now - created > TWO_DAYS_MS) todoIdle.push(t);
            }

            if (t.deadline) {
                const dl = new Date(t.deadline).getTime();
                if (dl < now) overdue.push(t);
                else if (dl - now <= TWO_DAYS_MS) nearDeadline.push(t);
            }
        }

        return [
            { key: "nearDeadline", label: "Sắp deadline", color: "#ff9800", tasks: nearDeadline },
            { key: "overdue", label: "Quá hạn", color: "#f44336", tasks: overdue },
            { key: "inProgress", label: "Đang làm", color: "#4da1ff", tasks: inProgress },
            { key: "todoIdle", label: "Chưa kéo", color: "#9c27b0", tasks: todoIdle },
        ];
    }, [tasks, userId, sprint._id]);

    const visibleCategories = categories.filter(c => c.tasks.length > 0);
    const expandedCategory = categories.find(c => c.key === expanded);

    return (
        <div style={{ padding: "8px 0", marginBottom: 4 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {visibleCategories.map((cat) => (
                    <div
                        key={cat.key}
                        onClick={() => setExpanded(expanded === cat.key ? null : cat.key)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "3px 8px",
                            borderRadius: 8,
                            background: expanded === cat.key ? `${cat.color}30` : `${cat.color}18`,
                            border: `1px solid ${expanded === cat.key ? cat.color : cat.color + "40"}`,
                            fontSize: 11,
                            color: cat.color,
                            cursor: "pointer",
                            transition: "all 0.15s",
                        }}
                    >
                        <span style={{ fontWeight: 700, fontSize: 13 }}>{cat.tasks.length}</span>
                        <span>{cat.label}</span>
                    </div>
                ))}
                {visibleCategories.length === 0 && (
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", padding: "3px 0" }}>
                        Không có task nào
                    </div>
                )}
            </div>

            {/* Danh sách task khi click */}
            {expandedCategory && expandedCategory.tasks.length > 0 && (
                <div style={{ marginTop: 6 }}>
                    {expandedCategory.tasks.map((t: any) => {
                        const shortId = t.shortId || "";
                        const title = t.title || "";
                        const taskId = t._id?.toString() || t.id;
                        const deadline = t.deadline
                            ? new Date(t.deadline).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" })
                            : "";
                        return (
                            <div
                                key={taskId}
                                onClick={() => window.open(`/task/${taskId}`)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "4px 8px",
                                    marginBottom: 2,
                                    borderRadius: 6,
                                    background: "rgba(255,255,255,0.04)",
                                    cursor: "pointer",
                                    fontSize: 11,
                                    transition: "background 0.1s",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.04)")}
                            >
                                <span style={{ color: "#4fc3f7", fontWeight: 600, minWidth: 70, flexShrink: 0 }}>{shortId}</span>
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "rgba(255,255,255,0.8)" }}>
                                    {title}
                                </span>
                                {deadline && (
                                    <span style={{ color: expandedCategory.color, fontSize: 10, flexShrink: 0 }}>{deadline}</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TaskOverviewWidget;
